'use client';

import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/* ──────────────────────────────────────────────
 *  LightDomSlot — projette ses enfants dans le Light DOM
 *  quand le composant est rendu dans un Shadow DOM.
 *
 *  Stripe Elements ne fonctionne pas dans un shadow root : l'iframe
 *  est injectée mais reste à 2 px de haut et n'émet jamais `ready`,
 *  sans aucune erreur (vérifié sur Chromium, inline et shadow
 *  imbriqué). Le contournement : un conteneur Light DOM, enfant
 *  direct de l'hôte du shadow, projeté visuellement à sa place via
 *  un `<slot>` nommé — le `<slot>` reste dans l'arbre shadow, donc
 *  dans le layout de son parent (display: contents).
 *
 *  Hors Shadow DOM (frontend-web, custom-websites), les enfants sont
 *  rendus tels quels : aucun changement de comportement.
 * ────────────────────────────────────────────── */

type SlotState = { kind: 'pending' } | { kind: 'light' } | { kind: 'shadow'; container: HTMLDivElement };

/* `useLayoutEffect` pour décider avant le premier paint (pas de flash) ; sans effet côté serveur. */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/* Le conteneur vit dans le Light DOM du site hôte : on neutralise l'héritage de ses styles. Les
 * enfants (wrapper + iframe Stripe) posent déjà leurs propres styles inline en `!important`. */
const CONTAINER_STYLE = 'all: initial; display: block; box-sizing: border-box; width: 100%;';

/* Sonde toujours rendue (même après décision) : la ref reste stable si l'effet est rejoué
 * (StrictMode) au lieu de retomber sur `undefined` → « light » à tort. */
const PROBE_STYLE = { display: 'none' } as const;

function isShadowRoot(node: Node | undefined): node is ShadowRoot {
  return typeof ShadowRoot !== 'undefined' && node instanceof ShadowRoot;
}

export function LightDomSlot({ children }: { children: ReactNode }) {
  const probeRef = useRef<HTMLSpanElement>(null);
  /* `useId` contient des caractères de ponctuation selon la version de React ; on garde un nom de
   * slot alphanumérique. L'unicité par arbre est préservée. */
  const slotName = `light-dom-slot-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const [state, setState] = useState<SlotState>({ kind: 'pending' });

  useIsomorphicLayoutEffect((): (() => void) | undefined => {
    const root = probeRef.current?.getRootNode();
    if (!isShadowRoot(root)) {
      setState({ kind: 'light' });
      return undefined;
    }
    const container = document.createElement('div');
    container.setAttribute('slot', slotName);
    container.style.cssText = CONTAINER_STYLE;
    root.host.appendChild(container);
    setState({ kind: 'shadow', container });
    return (): void => {
      container.remove();
    };
  }, [slotName]);

  return (
    <>
      <span ref={probeRef} aria-hidden="true" style={PROBE_STYLE} />
      {state.kind === 'light' && children}
      {state.kind === 'shadow' && (
        <>
          <slot name={slotName} />
          {createPortal(children, state.container)}
        </>
      )}
    </>
  );
}
